import { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Upload, Palette, Type, ChevronDown, Check, X } from 'lucide-react';
import OrbisSpinner from '@/components/ui/orbis-spinner';
import { ColorPicker } from '@/components/ui/color-picker';
import { GOOGLE_FONTS, FONT_CATEGORIES, preloadAllFonts } from '@/lib/fonts';
import { FontSearchPicker } from '@/components/ui/font-search-picker';
import DesignSystemPreview from '@/components/magic/DesignSystemPreview';

// ── Color extraction from image ──
function extractColorsFromImage(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = Math.min(img.width, 100);
        canvas.height = Math.min(img.height, 100);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

        // Sample pixels and find dominant colors
        const colorMap = {};
        for (let i = 0; i < data.length; i += 16) { // sample every 4th pixel
          const r = data[i], g = data[i+1], b = data[i+2], a = data[i+3];
          if (a < 128) continue; // skip transparent
          // Quantize to reduce noise
          const qr = Math.round(r / 32) * 32;
          const qg = Math.round(g / 32) * 32;
          const qb = Math.round(b / 32) * 32;
          const key = `${qr},${qg},${qb}`;
          colorMap[key] = (colorMap[key] || 0) + 1;
        }

        // Sort by frequency, skip near-white and near-black
        const sorted = Object.entries(colorMap)
          .filter(([k]) => {
            const [r,g,b] = k.split(',').map(Number);
            const lum = (r * 0.299 + g * 0.587 + b * 0.114);
            return lum > 30 && lum < 230; // skip near-black/white
          })
          .sort((a, b) => b[1] - a[1]);

        const toHex = (k) => {
          const [r,g,b] = k.split(',').map(Number);
          return '#' + [r,g,b].map(v => Math.min(255, v).toString(16).padStart(2, '0')).join('');
        };

        // Extract up to 5: primary, secondary, accent from mid-lum,
        // then find a dark (background candidate) and light (text candidate)
        const midColors = sorted.slice(0, 3).map(([k]) => toHex(k));

        // Find a dark color for background suggestion
        const darkEntry = Object.entries(colorMap)
          .filter(([k]) => {
            const [r,g,b] = k.split(',').map(Number);
            return (r * 0.299 + g * 0.587 + b * 0.114) < 50;
          })
          .sort((a, b) => b[1] - a[1])[0];
        const bgColor = darkEntry ? toHex(darkEntry[0]) : '#1a1a2e';

        // Find a light color for text suggestion
        const lightEntry = Object.entries(colorMap)
          .filter(([k]) => {
            const [r,g,b] = k.split(',').map(Number);
            return (r * 0.299 + g * 0.587 + b * 0.114) > 200;
          })
          .sort((a, b) => b[1] - a[1])[0];
        const textColor = lightEntry ? toHex(lightEntry[0]) : 'var(--orbis-warm)';

        const colors = [...midColors, bgColor, textColor].slice(0, 5);
        resolve(colors);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// Uses shared DesignSystemPreview component — same card styling as Design stage

// ── Font Selector (grouped by category with live preview) ──
function FontSelector({ label, value, onChange, isOptional }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Load selected font for preview
  useEffect(() => {
    if (!value) return;
    const id = `gfont-${value.replace(/\s+/g, '-')}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(value)}:wght@300;400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }, [value]);

  const query = search.toLowerCase();

  // Build filtered category groups
  const filteredCategories = Object.entries(FONT_CATEGORIES).map(([cat, fonts]) => ({
    category: cat,
    fonts: query ? fonts.filter(f => f.toLowerCase().includes(query)) : fonts,
  })).filter(g => g.fonts.length > 0);

  const totalResults = filteredCategories.reduce((n, g) => n + g.fonts.length, 0);

  return (
    <div className="relative" ref={dropdownRef}>
      <label className={`block text-xs mb-1.5 ${isOptional ? 'text-[var(--orbis-t4)]' : 'text-[var(--orbis-t3)]'}`}>
        {label} {isOptional && <span className="text-[var(--orbis-t4)]">(optional)</span>}
      </label>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-[var(--orbis-s1)] border border-[var(--orbis-s2)] text-sm text-[var(--orbis-t1)] hover:border-[var(--orbis-s3)] transition-colors"
      >
        <span style={{ fontFamily: value ? `"${value}", sans-serif` : 'inherit' }}>
          {value || 'Select font...'}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-[var(--orbis-t4)] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-[var(--orbis-s1)] border border-[var(--orbis-s2)] rounded-lg shadow-xl overflow-hidden"
             style={{ maxHeight: '250px', display: 'flex', flexDirection: 'column', bottom: 'auto' }}>
          {/* Search bar */}
          <div className="p-2 shrink-0" style={{ borderBottom: '1px solid var(--orbis-s2)' }}>
            <Input
              placeholder={`Search ${GOOGLE_FONTS.length} fonts...`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-8 text-xs bg-[var(--orbis-bg)] border-[var(--orbis-s2)] text-[var(--orbis-t1)]"
              autoFocus
            />
          </div>
          {/* Font list grouped by category */}
          <div className="p-1" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {value && !search && (
                <button
                  onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
                  className="w-full text-left px-3 py-1.5 text-xs text-[var(--orbis-t4)] hover:bg-[#FAFAFA08] rounded"
                >
                  Clear selection
                </button>
              )}
              {filteredCategories.map(({ category, fonts }) => (
                <div key={category}>
                  {/* Category header */}
                  <div className="px-3 pt-2.5 pb-1 flex items-center justify-between">
                    <span style={{
                      fontSize: '10px',
                      fontFamily: "'JetBrains Mono', var(--orbis-mono)",
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      color: 'rgba(240,239,232,0.35)',
                    }}>
                      {category}
                    </span>
                    <span style={{ fontSize: '9px', color: 'rgba(240,239,232,0.2)' }}>
                      {fonts.length}
                    </span>
                  </div>
                  {/* Font items with live preview */}
                  {fonts.map(font => (
                    <button
                      key={font}
                      onClick={() => { onChange(font); setOpen(false); setSearch(''); }}
                      onMouseEnter={() => {
                        // Lazy-load font on hover
                        const id = `gfont-${font.replace(/\s+/g, '-')}`;
                        if (document.getElementById(id)) return;
                        const link = document.createElement('link');
                        link.id = id;
                        link.rel = 'stylesheet';
                        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font)}:wght@400;700&display=swap`;
                        document.head.appendChild(link);
                      }}
                      className={`w-full text-left px-3 py-1.5 rounded transition-colors flex items-center justify-between ${
                        font === value
                          ? 'bg-[var(--orbis-s2)] text-[var(--orbis-t1)]'
                          : 'text-[var(--orbis-t3)] hover:bg-[var(--orbis-s2)] hover:text-[var(--orbis-t1)]'
                      }`}
                    >
                      <span className="text-sm truncate" style={{ fontFamily: `"${font}", sans-serif` }}>
                        {font}
                      </span>
                      {font === value && <Check className="w-3 h-3 shrink-0 ml-2" style={{ color: 'var(--orbis-blue)' }} />}
                    </button>
                  ))}
                </div>
              ))}
              {totalResults === 0 && (
                <div className="px-3 py-4 text-center text-xs text-[var(--orbis-t4)]">
                  No fonts match "{search}"
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Color Input — swatch card with ColorPicker popover ──
function ColorInput({ label, value, onChange, extracted, isOptional }) {
  const color = value || (isOptional ? '' : '#888888');
  const hasColor = !!value;
  const isLight = color.toLowerCase() === '#ffffff' || color.toLowerCase() === '#fff' || color.toLowerCase().startsWith('#f');

  return (
    <div>
      <ColorPicker
        value={color || '#888888'}
        onValueChange={(hex) => onChange(hex)}
        mode="solid"
      >
        <button className="w-full rounded-xl overflow-hidden cursor-pointer text-left transition-all hover:ring-1 hover:ring-[var(--orbis-s3)]" style={{ border: '1px solid var(--orbis-s2)' }}>
          <div style={{ height: 64, background: hasColor ? color : 'var(--orbis-s2)', position: 'relative' }}>
            {hasColor && (
              <span style={{
                position: 'absolute', bottom: 6, left: 8,
                fontSize: 9, fontWeight: 500, fontFamily: 'var(--orbis-mono)',
                color: isLight ? 'var(--orbis-bg)' : '#FFFFFF',
                opacity: 0.7,
              }}>
                {color.toUpperCase()}
              </span>
            )}
            {!hasColor && (
              <div className="flex items-center justify-center h-full">
                <span style={{ fontSize: 10, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)' }}>
                  {isOptional ? 'optional' : 'click to set'}
                </span>
              </div>
            )}
            {extracted && (
              <span className="absolute top-2 right-2 text-[8px] text-[var(--orbis-positive)] font-mono">✓</span>
            )}
          </div>
          <div style={{ padding: '6px 8px', background: 'var(--orbis-s1)' }}>
            <span style={{ fontSize: 10, color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-font)' }}>{label}</span>
          </div>
        </button>
      </ColorPicker>
    </div>
  );
}

// ── Main Component ──
export default function BrandFoundations({ onComplete, existingBrandContext }) {
  const [selectedPath, setSelectedPath] = useState(existingBrandContext?.brand_path || null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(existingBrandContext?.logo_url || null);
  const [colors, setColors] = useState({
    primary: existingBrandContext?.colors?.primary || '',
    secondary: existingBrandContext?.colors?.secondary || '',
    accent: existingBrandContext?.colors?.accent || '',
    background: existingBrandContext?.colors?.background || '',
    text: existingBrandContext?.colors?.text || '',
  });
  const [extractedColors, setExtractedColors] = useState({});
  const [headingFont, setHeadingFont] = useState(existingBrandContext?.heading_font || '');
  const [bodyFont, setBodyFont] = useState(existingBrandContext?.body_font || '');
  const [isDragging, setIsDragging] = useState(false);
  const [extractUrl, setExtractUrl] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractError, setExtractError] = useState(null);
  const [brandVoice, setBrandVoice] = useState(existingBrandContext?.brand_voice || null);
  const [contactHints, setContactHints] = useState(existingBrandContext?.contact_hints || null);
  const dropRef = useRef(null);

  // Preload all font previews on mount so picker is instant
  useEffect(() => { preloadAllFonts(); }, []);

  // Load Google Fonts for live preview
  useEffect(() => {
    const fonts = [headingFont, bodyFont].filter(Boolean);
    if (fonts.length === 0) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?${fonts.map(f => `family=${f.replace(/ /g, '+')}`).join('&')}&display=swap`;
    document.head.appendChild(link);
    return () => link.remove();
  }, [headingFont, bodyFont]);

  const handleLogoDrop = useCallback(async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setLogoFile(file);
    const url = URL.createObjectURL(file);
    setLogoPreview(url);

    // Extract colors
    const extracted = await extractColorsFromImage(file);
    if (extracted.length > 0) {
      const newColors = { ...colors };
      const newExtracted = {};
      const slots = ['primary', 'secondary', 'accent', 'background', 'text'];
      slots.forEach((slot, i) => {
        if (extracted[i] && !colors[slot]) {
          newColors[slot] = extracted[i];
          newExtracted[slot] = true;
        }
      });
      setColors(newColors);
      setExtractedColors(newExtracted);
    }
  }, [colors]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleLogoDrop(file);
  }, [handleLogoDrop]);

  const handleFileInput = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) handleLogoDrop(file);
  }, [handleLogoDrop]);

  const handleExtractBrand = useCallback(async () => {
    if (!extractUrl.trim()) return;
    setIsExtracting(true);
    setExtractError(null);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('orbis_token');
      const resp = await fetch(`${API_URL}/api/brand/extract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ url: extractUrl.trim(), brand_path: selectedPath }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Extraction failed');
      }
      const data = await resp.json();

      // Fill color hints
      if (data.color_hints) {
        const newColors = { ...colors };
        const newExtracted = { ...extractedColors };
        Object.entries(data.color_hints).forEach(([key, val]) => {
          if (val && !colors[key]) {
            newColors[key] = val;
            newExtracted[key] = true;
          }
        });
        setColors(newColors);
        setExtractedColors(newExtracted);
      }

      // Fill font hints
      if (data.font_hints) {
        if (data.font_hints.heading && !headingFont) setHeadingFont(data.font_hints.heading);
        if (data.font_hints.body && !bodyFont) setBodyFont(data.font_hints.body);
      }

      // Fill logo
      if (data.logo_url && !logoPreview) {
        setLogoPreview(data.logo_url);
      }

      // Store brand voice
      if (data.brand_voice) {
        setBrandVoice(data.brand_voice);
      }

      // Store contact hints (only returned for partial/full paths)
      if (data.contact_hints) {
        setContactHints(data.contact_hints);
      }
    } catch (e) {
      setExtractError(e.message || 'Could not analyze that website. Try filling in manually.');
    } finally {
      setIsExtracting(false);
    }
  }, [extractUrl, colors, extractedColors, headingFont, bodyFont, logoPreview]);

  const handleContinue = () => {
    const brandContext = {
      brand_path: selectedPath,
      logo_url: logoPreview,
      colors: (selectedPath !== 'none' && (colors.primary || colors.secondary || colors.accent || colors.background || colors.text))
        ? colors : null,
      heading_font: headingFont || null,
      body_font: bodyFont || null,
      brand_voice: brandVoice || null,
      contact_hints: contactHints || null,
    };
    onComplete(brandContext);
  };

  const hasAnyInput = logoPreview || colors.primary || headingFont || bodyFont;
  const canContinue = selectedPath === 'none' || (selectedPath && hasAnyInput);

  const CARDS = [
    {
      id: 'none',
      eyebrow: 'NO BRAND YET',
      headline: 'Build from scratch.',
      body: 'Orbis selects your colors, fonts, and visual direction. Tell us your industry and we handle the rest.',
    },
    {
      id: 'partial',
      eyebrow: 'PARTIAL BRAND',
      headline: 'I have some elements.',
      body: 'A logo, a color, a font. Drop them in and Orbis builds the rest around what you already have.',
    },
    {
      id: 'full',
      eyebrow: 'FULL BRAND KIT',
      headline: 'My brand is defined.',
      body: 'Colors, fonts, and logo locked. Orbis generates layout intelligence and page architecture on top of your brand.',
    },
  ];

  // Brand Elements (logo, fonts, colors) only for partial/full paths
  // Brand Intelligence (URL analyzer) always shows — useful even with "No Brand Yet"
  const showBrandElements = selectedPath === 'partial' || selectedPath === 'full';

  return (
    <div className="flex-1 overflow-y-auto" style={{ height: 'calc(100vh - 64px)' }}>
      <div className="w-full max-w-5xl mx-auto px-8 py-10 pb-24">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-[11px] font-mono uppercase tracking-[0.1em] text-[var(--orbis-t3)] mb-3">
            01 · Brand Foundations
          </p>
          <h1 className="text-2xl md:text-3xl font-light text-[var(--orbis-t1)] tracking-tight">
            Tell us about your brand.
          </h1>
        </div>

        {/* Choice Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          {CARDS.map(card => (
            <button
              key={card.id}
              onClick={() => setSelectedPath(card.id)}
              className={`text-left p-5 rounded-[18px] border transition-all duration-200 ${
                selectedPath === card.id
                  ? 'bg-[var(--orbis-s2)] border-[var(--orbis-s3)]'
                  : 'bg-[var(--orbis-s1)] border-[var(--orbis-s2)] hover:border-[var(--orbis-s3)]'
              }`}
            >
              <p className="text-[10px] font-mono uppercase tracking-[0.1em] text-[var(--orbis-t4)] mb-2">
                {card.eyebrow}
              </p>
              <h3 className="text-[16px] font-light text-[var(--orbis-t1)] mb-2">{card.headline}</h3>
              <p className="text-[14px] text-[var(--orbis-t3)] leading-relaxed">{card.body}</p>
            </button>
          ))}
        </div>

        {/* Brand Intelligence — always visible regardless of path */}
        {selectedPath && (
          <div className="mb-4">
            <div className="bg-[var(--orbis-s1)] rounded-[18px] border border-[var(--orbis-s2)] p-6">
              <p className="text-[11px] font-mono uppercase tracking-[0.1em] text-[var(--orbis-t3)] mb-4">
                Brand Intelligence
              </p>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[13px] text-[rgba(240,239,232,0.55)]">
                  Have an existing website? We'll analyze your brand voice and extract visual elements.
                </span>
              </div>
              <div className="flex gap-3">
                <input
                  type="url"
                  value={extractUrl}
                  onChange={e => { setExtractUrl(e.target.value); setExtractError(null); }}
                  onKeyDown={e => e.key === 'Enter' && handleExtractBrand()}
                  placeholder="https://yoursite.com"
                  disabled={isExtracting}
                  className="flex-1 h-11 px-4 text-[14px] bg-[var(--orbis-s1)] border rounded-lg text-[var(--orbis-t1)] outline-none focus:border-[rgba(184,204,223,0.35)] transition-colors disabled:opacity-50"
                  style={{ borderColor: 'var(--orbis-s2)' }}
                />
                <button
                  onClick={handleExtractBrand}
                  disabled={isExtracting || !extractUrl.trim()}
                  className="h-11 px-5 rounded-full text-[14px] font-light transition-opacity disabled:opacity-30 shrink-0"
                  style={{ background: 'var(--orbis-blue)', color: '#111' }}
                >
                  {isExtracting ? (
                    <span className="flex items-center gap-2">
                      <OrbisSpinner size={16} color="#09090B" />
                      Analyzing...
                    </span>
                  ) : 'Analyze →'}
                </button>
              </div>
              <p className="text-[13px] text-[rgba(240,239,232,0.35)] mt-2">
                We'll learn your brand voice, messaging style, and extract visual hints
              </p>
              {extractError && (
                <p className="text-[13px] text-[#ef4444] mt-2">{extractError}</p>
              )}
              {/* Brand voice summary — full rich display */}
              {brandVoice && (
                <div className="mt-4 pt-4" style={{ borderTop: '0.5px solid var(--orbis-s2)' }}>
                  {/* Header row: check + industry + tone badges */}
                  <div className="flex items-center flex-wrap gap-2 mb-3">
                    <span className="text-[13px] text-[var(--orbis-green)] font-medium">✓ Brand voice detected</span>
                    {brandVoice.industry && (
                      <span style={{ fontSize: 10, fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '2px 8px', borderRadius: 100, background: '#27272A60', color: 'var(--orbis-t3)' }}>
                        {brandVoice.industry}
                      </span>
                    )}
                    {brandVoice.tone_of_voice && (
                      <span style={{ fontSize: 10, fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '2px 8px', borderRadius: 100, background: '#27272A60', color: 'var(--orbis-t3)' }}>
                        {brandVoice.tone_of_voice}
                      </span>
                    )}
                  </div>

                  {/* Company name + description */}
                  {brandVoice.company_name && (
                    <p style={{ fontSize: 15, fontWeight: 500, color: 'var(--orbis-t1)', marginBottom: 4 }}>
                      {brandVoice.company_name}
                    </p>
                  )}
                  {brandVoice.company_description && (
                    <p style={{ fontSize: 13, color: 'var(--orbis-t3)', lineHeight: 1.5, marginBottom: 8, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {brandVoice.company_description}
                    </p>
                  )}

                  {/* Tagline */}
                  {brandVoice.tagline && (
                    <p style={{ fontSize: 13, fontStyle: 'italic', color: 'rgba(240,239,232,0.55)', marginBottom: 8 }}>
                      "{brandVoice.tagline}"
                    </p>
                  )}

                  {/* Audience + writing style */}
                  {(brandVoice.target_audience || brandVoice.writing_style) && (
                    <div style={{ fontSize: 12, color: 'var(--orbis-t4)', marginBottom: 10, lineHeight: 1.5 }}>
                      {brandVoice.target_audience && <span>Audience: {brandVoice.target_audience}</span>}
                      {brandVoice.target_audience && brandVoice.writing_style && <span> · </span>}
                      {brandVoice.writing_style && <span>Style: {brandVoice.writing_style}</span>}
                    </div>
                  )}

                  {/* Personality traits (green pills) */}
                  {brandVoice.personality_traits?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {brandVoice.personality_traits.slice(0, 4).map((trait, i) => (
                        <span key={i} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 100, background: 'rgba(143,196,160,0.12)', color: 'var(--orbis-green)', border: '1px solid rgba(143,196,160,0.2)' }}>
                          {trait}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Key messages (grey pills) */}
                  {brandVoice.key_messages?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {brandVoice.key_messages.slice(0, 3).map((msg, i) => (
                        <span key={i} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 100, background: '#27272A60', color: 'var(--orbis-t3)', border: '1px solid #27272A60' }}>
                          {msg.length > 50 ? msg.slice(0, 50) + '…' : msg}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Style vibe */}
                  {brandVoice.style_vibe && (
                    <p style={{ fontSize: 11, fontStyle: 'italic', color: 'var(--orbis-t4)', lineHeight: 1.5, marginTop: 6 }}>
                      {brandVoice.style_vibe}
                    </p>
                  )}

                  {/* Contact info — only for partial/full paths */}
                  {contactHints && selectedPath !== 'none' && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '0.5px solid var(--orbis-s2)' }}>
                      {/* Location, email, phone row */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1" style={{ fontSize: 12, color: 'var(--orbis-t4)' }}>
                        {contactHints.location && (
                          <span>📍 {contactHints.location}</span>
                        )}
                        {contactHints.email && (
                          <span>✉ {contactHints.email}</span>
                        )}
                        {contactHints.phone && (
                          <span>📞 {contactHints.phone}</span>
                        )}
                      </div>
                      {/* Social icon links */}
                      {contactHints.social_links && Object.entries(contactHints.social_links).some(([, v]) => v) && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {[
                            { key: 'instagram', label: 'IG', color: '#E1306C' },
                            { key: 'twitter',   label: '𝕏',  color: 'var(--orbis-t3)' },
                            { key: 'linkedin',  label: 'in', color: '#0A66C2' },
                            { key: 'facebook',  label: 'f',  color: '#1877F2' },
                            { key: 'youtube',   label: '▶',  color: '#FF0000' },
                            { key: 'tiktok',    label: '♪',  color: 'var(--orbis-t3)' },
                            { key: 'pinterest', label: 'P',  color: '#E60023' },
                          ].filter(s => contactHints.social_links[s.key]).map(s => (
                            <a
                              key={s.key}
                              href={contactHints.social_links[s.key]}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                width: 28, height: 28, borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 11, fontWeight: 600, background: '#27272A',
                                color: s.color, border: '1px solid #3F3F46',
                                textDecoration: 'none', flexShrink: 0,
                              }}
                              title={s.key}
                            >
                              {s.label}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Brand Elements — only for partial/full paths */}
        {showBrandElements && (
          <div className="space-y-4">
            {/* Card 2: Brand Elements — Logo, Fonts, Colors */}
            <div className="bg-[var(--orbis-s1)] rounded-[18px] border border-[var(--orbis-s2)] p-6">
              <p className="text-[11px] font-mono uppercase tracking-[0.1em] text-[var(--orbis-t3)] mb-4">
                Brand Elements
              </p>

            {/* Row 1: Logo + Fonts side by side */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-6 mb-6">
              {/* Logo drop zone */}
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-[0.1em] text-[var(--orbis-t3)] mb-2">Logo</label>
                <div
                  ref={dropRef}
                  onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  className={`relative flex items-center justify-center h-[88px] rounded-lg border border-dashed transition-colors cursor-pointer ${
                    isDragging
                      ? 'border-[var(--orbis-blue)] bg-[rgba(184,204,223,0.05)]'
                      : 'border-[var(--orbis-s2)] hover:border-[var(--orbis-s3)]'
                  }`}
                >
                  {logoPreview ? (
                    <div className="flex items-center gap-4">
                      <img src={logoPreview} alt="Logo" className="max-h-14 max-w-[120px] object-contain" />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setLogoPreview(null);
                          setLogoFile(null);
                        }}
                        className="p-1 rounded-full hover:bg-[var(--orbis-s3)]"
                      >
                        <X className="w-3.5 h-3.5 text-[var(--orbis-t4)]" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-4 h-4 text-[var(--orbis-t4)] mx-auto mb-1" />
                      <p className="text-xs text-[var(--orbis-t4)]">Drop your logo here</p>
                      <p className="text-[10px] text-[var(--orbis-t4)]/60 mt-0.5">PNG, SVG, or JPG</p>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileInput}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    style={{ pointerEvents: logoPreview ? 'none' : 'auto' }}
                  />
                </div>
              </div>

              {/* Fonts */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl p-4" style={{ border: '1px solid var(--orbis-s2)', background: 'var(--orbis-s1)' }}>
                  <span style={{ fontSize: 11, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}>Heading</span>
                  {headingFont ? (
                    <>
                      <p className="mt-2" style={{ fontSize: 22, fontWeight: 600, color: 'var(--orbis-t1)', lineHeight: 1.2, fontFamily: `"${headingFont}", sans-serif` }}>
                        {headingFont}
                      </p>
                      <p className="mt-1.5" style={{ fontSize: 13, color: 'var(--orbis-t4)', fontFamily: `"${headingFont}", sans-serif` }}>
                        Aa Bb Cc Dd Ee Ff Gg
                      </p>
                    </>
                  ) : (
                    <p className="mt-2 mb-1" style={{ fontSize: 13, color: 'var(--orbis-s3)' }}>No heading font</p>
                  )}
                  <div className="mt-3">
                    <FontSearchPicker value={headingFont} onChange={setHeadingFont} />
                  </div>
                </div>
                <div className="rounded-xl p-4" style={{ border: '1px solid var(--orbis-s2)', background: 'var(--orbis-s1)' }}>
                  <span style={{ fontSize: 11, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)' }}>Body</span>
                  {bodyFont ? (
                    <>
                      <p className="mt-2" style={{ fontSize: 18, fontWeight: 400, color: 'var(--orbis-t1)', lineHeight: 1.2, fontFamily: `"${bodyFont}", sans-serif` }}>
                        {bodyFont}
                      </p>
                      <p className="mt-1.5" style={{ fontSize: 13, color: 'var(--orbis-t4)', fontFamily: `"${bodyFont}", sans-serif` }}>
                        Aa Bb Cc Dd Ee Ff Gg
                      </p>
                    </>
                  ) : (
                    <p className="mt-2 mb-1" style={{ fontSize: 13, color: 'var(--orbis-s3)' }}>No body font</p>
                  )}
                  <div className="mt-3">
                    <FontSearchPicker value={bodyFont} onChange={setBodyFont} />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Colors — 5 tokens in a single row */}
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-[0.1em] text-[var(--orbis-t3)] mb-2">Colors</label>
              <div className="grid grid-cols-5 gap-3">
                <ColorInput
                  label="Primary"
                  value={colors.primary}
                  onChange={v => setColors(c => ({ ...c, primary: v }))}
                  extracted={extractedColors.primary}
                />
                <ColorInput
                  label="Secondary"
                  value={colors.secondary}
                  onChange={v => setColors(c => ({ ...c, secondary: v }))}
                  extracted={extractedColors.secondary}
                  isOptional={selectedPath === 'partial'}
                />
                <ColorInput
                  label="Accent"
                  value={colors.accent}
                  onChange={v => setColors(c => ({ ...c, accent: v }))}
                  extracted={extractedColors.accent}
                  isOptional
                />
                <ColorInput
                  label="Background"
                  value={colors.background}
                  onChange={v => setColors(c => ({ ...c, background: v }))}
                  extracted={extractedColors.background}
                  isOptional
                />
                <ColorInput
                  label="Text"
                  value={colors.text}
                  onChange={v => setColors(c => ({ ...c, text: v }))}
                  extracted={extractedColors.text}
                  isOptional
                />
              </div>
            </div>

            {/* Component Specimens — live brand preview */}
            {hasAnyInput && (
              <div className="mt-6 pt-5 border-t border-[#27272A60]">
                <p className="text-[10px] font-mono uppercase tracking-[0.1em] text-[var(--orbis-t4)] mb-1">
                  Live Preview
                </p>
                <p className="text-[11px] text-[var(--orbis-t4)] mb-3">
                  Orbis will build the full design system around these. Your colors, fonts, and logo stay locked.
                </p>
                <DesignSystemPreview
                  colors={colors}
                  headingFont={headingFont}
                  bodyFont={bodyFont}
                  className="grid grid-cols-2 gap-4"
                />
              </div>
            )}
            </div>
          </div>
        )}

        {/* Continue button */}
        {selectedPath && (
          <div className="flex justify-center mt-8">
            <Button
              onClick={handleContinue}
              disabled={!canContinue}
              className="bg-[var(--orbis-blue)] text-[var(--orbis-bg)] hover:bg-[var(--orbis-blue)]/80 px-8 py-3 rounded-full text-[14px] font-light min-w-[180px]"
            >
              Continue →
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
